import type { Vec2, Vec3, Ring } from '../../geo/types';
import type { Frame, MeshBuilder, VertexStyle } from '../mesh/meshBuilder';
import { ensureCCW } from '../../geo/polygon';
import { triangulateRing } from './solids';

/**
 * A planar wall face with flush window openings. Panel coordinates (a, b) run along
 * uAxis and vAxis; the face points along uAxis x vAxis.
 */
export interface Panel {
  readonly origin: Vec3;
  readonly uAxis: Vec3;
  readonly vAxis: Vec3;
}

export interface PanelStyles {
  readonly wall: VertexStyle;
  readonly opening: VertexStyle;
}

const EQUILATERAL_ARCH_SWEEP = Math.PI / 3;

/** Vertical panel facing a horizontal outward direction, b measured up from origin. */
export function verticalPanel(origin: Vec3, outward: Vec3): Panel {
  return { origin, uAxis: [outward[2], 0, -outward[0]], vAxis: [0, 1, 0] };
}

export function panelPoint(panel: Panel, a: number, b: number): Vec3 {
  const { origin: o, uAxis: u, vAxis: v } = panel;
  return [o[0] + u[0] * a + v[0] * b, o[1] + u[1] * a + v[1] * b, o[2] + u[2] * a + v[2] * b];
}

/** Metres along a panel's u axis and up its v axis from its origin. */
export function panelUv(panel: Panel): (p: Vec3) => Vec2 {
  const { origin: o, uAxis: u, vAxis: v } = panel;
  return (p) => {
    const d = [p[0] - o[0], p[1] - o[1], p[2] - o[2]] as const;
    return [d[0] * u[0] + d[1] * u[1] + d[2] * u[2], d[0] * v[0] + d[1] * v[1] + d[2] * v[2]];
  };
}

function emitPolygon(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  panel: Panel,
  pts: readonly Vec2[]
): void {
  const local = pts.map((p) => panelPoint(panel, p[0], p[1]));
  builder.polygon(style, frame, local, panelUv(panel));
}

/** Wall outline minus openings, triangulated with earcut; openings are convex and flush. */
export function panelWall(
  builder: MeshBuilder,
  styles: PanelStyles,
  frame: Frame,
  panel: Panel,
  outline: Ring,
  openings: readonly Ring[]
): void {
  const holes = openings.map(ensureCCW);
  for (const tri of triangulateRing(ensureCCW(outline), holes)) {
    emitPolygon(builder, styles.wall, frame, panel, tri);
  }
  for (const hole of holes) {
    emitPolygon(builder, styles.opening, frame, panel, hole);
  }
}

/** Gothic window: straight jambs to springB, then an equilateral pointed arch. */
export function pointedWindow(
  centerA: number,
  sillB: number,
  width: number,
  springB: number,
  segments: number
): Ring {
  const half = width / 2;
  const right: Vec2[] = [];
  const left: Vec2[] = [];
  for (let k = 1; k < segments; k++) {
    const angle = (EQUILATERAL_ARCH_SWEEP * k) / segments;
    right.push([centerA - half + width * Math.cos(angle), springB + width * Math.sin(angle)]);
    left.unshift([centerA + half - width * Math.cos(angle), springB + width * Math.sin(angle)]);
  }
  return [
    [centerA - half, sillB],
    [centerA + half, sillB],
    [centerA + half, springB],
    ...right,
    [centerA, springB + width * Math.sin(EQUILATERAL_ARCH_SWEEP)],
    ...left,
    [centerA - half, springB],
  ];
}

export function pointedArchRise(width: number): number {
  return width * Math.sin(EQUILATERAL_ARCH_SWEEP);
}

/** Round-headed opening: straight jambs to springB, then a semicircle. */
export function roundArch(
  centerA: number,
  sillB: number,
  width: number,
  springB: number,
  segments: number
): Ring {
  const half = width / 2;
  const arc = Array.from({ length: segments - 1 }, (_, i): Vec2 => {
    const angle = (Math.PI * (i + 1)) / segments;
    return [centerA + half * Math.cos(angle), springB + half * Math.sin(angle)];
  });
  return [
    [centerA - half, sillB],
    [centerA + half, sillB],
    [centerA + half, springB],
    ...arc,
    [centerA - half, springB],
  ];
}
